// src/utils/costiDalVivo.js
// ═══════════════════════════════════════════════════════════════════════════
// ORE E COSTI CALCOLATI DI UNA COMMESSA, LETTI ADESSO (W453, 31/10/2026)
// ═══════════════════════════════════════════════════════════════════════════
// Manodopera da Programmazione/Rapportini, ore di «Le mie ore» e noleggi mezzi d'opera non sono
// movimenti: prima li calcolava solo il Controllo Economico, che li salva sul budget quando si
// apre. I documenti (report, Relazione finale) li leggevano da lì, quindi valevano «all'ultima
// apertura del Controllo Economico». Qui si leggono dal vivo, con le stesse regole
// (utils/oreCommessa.js), e totaliCommessa (utils/economiaCommessa.js) li somma allo stesso modo.
// ═══════════════════════════════════════════════════════════════════════════
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { fonteOreAzienda, oreDaProgrammazione, oreDaRapportini, oreDaPresenze, vociConDiario, costiCalcolatiDaVoci, unisciCostiCalcolati } from "./oreCommessa";
export { unisciCostiCalcolati };
import { costoNoleggio } from "./noleggiUtils";

/**
 * Ore della commessa dalla fonte dell'azienda, unite al diario «Le mie ore».
 * → { fonte, voci, dipendenti }   (voci: { chiave, nome, data, ore, rif, … })
 */
export async function caricaVociOre({ orgId, projectId, org = {} }) {
  const fonte = fonteOreAzienda(org);
  let voci = [];
  if (fonte === "rapportini") {
    const snap = await getDocs(query(collection(db, "rapportini"), where("projectId", "==", projectId), where("orgId", "==", orgId)));
    voci = oreDaRapportini(snap.docs.map(d => d.data()), org.rapportiniOrePrecedenti || "durata");
  } else if (fonte === "presenze") {
    const snap = await getDocs(query(collection(db, "movimentiEconomici"), where("projectId", "==", projectId), where("organizationId", "==", orgId)));
    voci = oreDaPresenze(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  } else {
    const [progSnap, utSnap] = await Promise.all([
      getDocs(query(collection(db, "programmazioniSettimanali"), where("organizationId", "==", orgId))),
      getDocs(query(collection(db, "users"), where("organizationId", "==", orgId))).catch(() => ({ docs: [] })),
    ]);
    const nomi = Object.fromEntries(utSnap.docs.map(d => [d.id, d.data().displayName || ""]));
    voci = oreDaProgrammazione(progSnap.docs.map(d => d.data()), projectId, nomi);
  }
  // diario «Le mie ore» (W403): se non è leggibile da chi genera, si resta come prima
  const [diarioSnap, dipSnap] = await Promise.all([
    getDocs(query(collection(db, "diarioOre"), where("orgId", "==", orgId), where("projectId", "==", projectId))).catch(() => null),
    getDocs(query(collection(db, "dipendenti"), where("organizationId", "==", orgId))).catch(() => ({ docs: [] })),
  ]);
  const dipendenti = dipSnap.docs.map(d => d.data());
  voci = vociConDiario(voci, fonte, { blocchi: diarioSnap ? diarioSnap.docs.map(d => d.data()) : null, dipendenti });
  return { fonte, voci, dipendenti, diarioLeggibile: !!diarioSnap, dipendentiLeggibili: dipSnap.docs.length > 0 };
}

/** Costo dei noleggi mezzi d'opera della commessa, calcolato adesso (come il Controllo Economico). */
export async function caricaCostoNoleggi({ orgId, projectId }) {
  const snap = await getDocs(query(collection(db, "noleggi"), where("projectId", "==", projectId), where("orgId", "==", orgId), where("categoria", "==", "mezzo_opera")))
    .catch(() => ({ docs: [] }));
  return snap.docs.reduce((t, d) => t + costoNoleggio(d.data()), 0);
}

/**
 * costiCalcolati della commessa adesso, nella forma di budget.costiCalcolati.
 * Se qualcosa non si può leggere restituisce null: chi chiama usa quelli salvati sul budget.
 */
export async function costiCalcolatiDalVivo({ orgId, projectId, org = {}, vociOre = null, salvati = null }) {
  try {
    const [ore, noleggi] = await Promise.all([vociOre ? Promise.resolve(vociOre) : caricaVociOre({ orgId, projectId, org }), caricaCostoNoleggi({ orgId, projectId })]);
    return unisciCostiCalcolati(costiCalcolatiDaVoci({ voci: ore.voci, dipendenti: ore.dipendenti, fonteOre: ore.fonte, noleggi }), ore, salvati);
  } catch (err) {
    console.warn("Costi calcolati dal vivo non disponibili:", err?.code || err);
    return null;
  }
}

